import { Link } from "@/components/LangLink";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES, SERVICES } from "@/data/site";
import { tr } from "@/i18n";

export default function Services() {
  const featured = SERVICES.filter((s) => s.image);
  const additional = SERVICES.filter((s) => !s.image);

  return (
    <div data-testid="services-page">
      <PageHero
        overline={tr("Our Services")}
        title={tr("Anything you require,")}
        italic={tr("already arranged.")}
        image={IMAGES.yacht}
      >
        <p>{tr("From a dog groomer at your doorstep to private travel to the most exclusive hotspots — we specialise in both luxury needs and daily necessities, and take pride in covering absolutely anything.")}</p>
      </PageHero>

      <section className="py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-20">
            {featured.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "sm:mt-24" : ""}>
                <article id={s.id} data-testid={`service-card-${s.id}`} className="group scroll-mt-28">
                  <Link to={`/services/${s.id}`} className="block">
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={s.image}
                        alt={tr(s.name)}
                        className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <span className="absolute top-6 left-6 font-serif italic text-white/40 text-lg">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </Link>
                  <div className="pt-8 border-b border-white/10 pb-10">
                    <h2 className="font-serif text-3xl font-light">{tr(s.name)}</h2>
                    <p className="text-gold font-serif italic text-lg mt-2">{tr(s.tagline)}</p>
                    <p className="text-white/55 font-light leading-relaxed mt-4">{tr(s.description)}</p>
                    <Link
                      to={`/contact?service=${s.id}`}
                      data-testid={`service-enquire-${s.id}`}
                      className="group/link inline-flex items-center gap-2 mt-6 text-[0.65rem] uppercase tracking-[0.3em] text-white/60 hover:text-gold transition-colors duration-300"
                    >{tr("Enquire")}<ArrowUpRight strokeWidth={1.5} size={14} className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {additional.length > 0 && (
          <div className="mt-32">
            <Reveal>
              <p className="overline-lux mb-12">{tr("Furthermore")}</p>
            </Reveal>
            <div>
              {additional.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.06}>
                  <Link
                    to={`/services/${s.id}`}
                    id={s.id}
                    data-testid={`service-row-${s.id}`}
                    className="group grid grid-cols-12 items-baseline gap-4 py-8 border-t border-white/10 hover:border-gold/40 transition-colors duration-500 scroll-mt-28"
                  >
                    <span className="col-span-2 sm:col-span-1 font-serif italic text-white/30">
                      {String(featured.length + i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="col-span-10 sm:col-span-4 font-serif text-2xl sm:text-3xl font-light group-hover:text-gold transition-colors duration-500">
                      {tr(s.name)}
                    </h3>
                    <p className="col-span-10 col-start-3 sm:col-span-6 sm:col-start-6 text-white/50 font-light text-sm leading-relaxed">
                      {tr(s.description)}
                    </p>
                    <ArrowUpRight
                      strokeWidth={1.25}
                      size={20}
                      className="hidden sm:block col-span-1 justify-self-end text-white/30 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          )}
        </div>
      </section>
    </div>
  );
}
